import { state, style, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { PayloadHelper } from '@app/api/core/utils/helper/payload-helper';
import { AdditionalErfassenTyp } from '@app/shared/components/erfassen/additional-erfassen-typ';
import { NotizComponent } from '@app/shared/components/form/notiz/notiz.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { ReminderComponent } from '@app/shared/components/reminder/reminder.component';
import { ThrottleEventDirective } from '@app/shared/directives/throttle-event.directive';
import { FormatAdressPipe } from '@app/shared/pipes/formatadress.pipe';
import { FormatDatePipe } from '@app/shared/pipes/formatdate.pipe';
import { ActiveTab } from '@app/shared/services/state/model/o30/activetab';
import { DialogStatus } from '@app/shared/services/ui/dialog/dialog.types';
import { DisplayOption, MessageType } from '@app/shared/services/ui/message/message';
import { ExpandableRow, ListTyp } from '@app/views/vertriebscockpit/offerteantraege/offerteantraege.types';
import { AntragRejectType, Notiz, Payload, PolizzeResult, RightType } from '@o30/entities';
import { Observable } from 'rxjs';
import { filter, first, switchMap, take } from 'rxjs/operators';
import { BasicPolizzeListComponent } from '../basic-polizze-list/basic-polizze-list.component';

@Component({
  selector:    'app-offertstageone',
  templateUrl: './offertstageone.component.html',
  styleUrls:   ['./offertstageone.component.scss'],
  animations:  [
    trigger('detailExpand', [
      state('collapsed', style({
        display: 'none'
      })),
      state('expanded', style({
        height:     '*',
        visibility: 'visible'
      }))
    ])
  ],
  standalone:  true,
  imports:     [
    NgIf,
    MatTable,
    MatSort,
    NgFor,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    MatTooltip,
    ThrottleEventDirective,
    ReminderComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    MatPaginator,
    MatProgressSpinner,
    NoDataComponent,
    AsyncPipe,
    FormatDatePipe,
    FormatAdressPipe
  ]
})

export class OffertstageoneComponent extends BasicPolizzeListComponent implements OnInit,
                                                                                  OnDestroy {

  private currentPolNr: number;

  private rightsConfig = {
    sendenErlaubt:             false,
    beratungEmpfehlungErlaubt: false,
    rejectStatusVisible:       false
  };

  constructor() {
    super();
    this.applyRightConfigByPrivilege('O30_POLICE_EXTERN', 'sendenErlaubt');
    this.applyRightConfigByPrivilege('O30_BERATUNG_EMPFEHLUNG', 'beratungEmpfehlungErlaubt');
    this.applyRightConfigByPrivilege('O30_OFFERT_REJECT_STATUS', 'rejectStatusVisible');
  }

  private applyRightConfigByPrivilege(rightId: RightType, property: keyof typeof this.rightsConfig) {
    this.userService.isAuthorized$(rightId)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(_hasRight => this.rightsConfig[property] = _hasRight);
  }

  public createOffert2(row: PolizzeResult): void {
    this.llapi.patchValue('layout.spinner', true);
    this.dataService.savePolizzeOffert2(row.id)
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(payload => {
        this.llapi.patchValue('layout.spinner', false);
        if (payload && !PayloadHelper.hasError(payload)) {
          this.listData.reloadListSubject.next(ListTyp.BERATUNG_EMPFEHLUNG); // Beratung/Empfehlung Liste neu laden
          this.messageService.handlePayload(payload, {displayOption: DisplayOption.SNACKBAR});
          this.llapi.patchValue('layout.activeTab', ActiveTab.OfferteAntraege_BEREMPF);
        } else if (payload) {
          this.messageService.handlePayload(payload, {displayOption: DisplayOption.MONITOR});
        }
      });
  }

  private putPolizzeNotizAndUpdateBearbeitet(notiz: Notiz): Observable<Payload<number>> {
    return this.dataService.putPolizzeNotiz(this.currentPolNr, notiz);
  }

  private getPolizzeNotiz(): Observable<Payload<Notiz>> {
    return this.dataService.getPolizzeNotiz(this.currentPolNr);
  }

  public editNotiz(row: PolizzeResult): void {
    this.currentPolNr = row.id;
    this.dialogService.displayErfassenDialog({
        additionalErfassenTyp: AdditionalErfassenTyp.CREATE,
        createErfassenConfig:  {
          createType:                NotizComponent,
          createContext:             null,
          createValidators:          new Map(),
          createSaveCallback:        this.putPolizzeNotizAndUpdateBearbeitet.bind(this),
          createCustomHeader:        'Notiz',
          createModelGetterCallback: this.getPolizzeNotiz.bind(this)
        },
        disableCancelConfirm:  true
      })
      .afterClosed()
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(_dialogErfassenResult => {
        if (_dialogErfassenResult && _dialogErfassenResult.statusId === DialogStatus.SAVE_SUCCESS) {
          this.messageService.showSnackBar({
            text: 'Notiz erfolgreich gespeichert',
            type: MessageType.SUCCESS,
            icon: 'done_outline'
          });
          if (_dialogErfassenResult.data && (<Notiz>_dialogErfassenResult.data).text) {
            row.notiz = (<Notiz>_dialogErfassenResult.data).text.value;
          }
        }

      });
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--override superclass--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  // @Override
  get displayedColumns() {
    const columns = this.tableColumns.map(c => c.columnDef);
    columns.unshift('expand');
    // Wenn nicht berechtigt → Spalten nicht anzeigen
    if (this.bearbeitenErlaubt) {
      columns.push('aktionen');
    }
    if (this.rightsConfig.sendenErlaubt) {
      columns.push('sendToMakler');

    }
    if (this.rightsConfig.rejectStatusVisible) {
      columns.push('reject-status');
    }
    if (this.rightsConfig.beratungEmpfehlungErlaubt) {
      columns.push('nextoffert');
    }
    return columns;
  }

  getAdditionalColumns() {
    return [
      {
        columnDef: 'variante',
        header:    'Offert Variante',
        cell:      (row: ExpandableRow<PolizzeResult>) => `${row.data.variante}` + (row.data.notiz ? '\n' + (row.data.notiz.length > 10 ? row.data.notiz.substring(0, 10) + '...' : row.data.notiz) : '')
      }
    ];
  }

  getAnzRowsPerEntry(): number {
    return 2;
  }

  async sendDownloadOffert(row: PolizzeResult) {
    if (this.isMakler()) {
      await this.peziService.openPeziExternerMakler(`maklerpolizze/${row.id}?openPrintDialog=printSendOffert`);
    } else {
      this.peziService.openPeziWithLogin(`/polizze/${row.id}?openPrintDialog=printSendOffert`, this.destroyRef);
    }
  }

  sendRevokeMaklerAccess(polizze: PolizzeResult) {
    if (polizze.intern) {
      this.sendToMakler(polizze);
    } else {
      this.revokeMaklerAccess(polizze);
    }
  }

  sendToMakler(polizze: PolizzeResult): void {
    this.callBackendFunctionIfConfirmed(polizze, false,
      this.dataService.sendPoliceToMakler.bind(this.dataService),
      'Wollen Sie das Offert für den externen Makler freigeben?',
      'Polizze ist extern zugänglich');
  }

  revokeMaklerAccess(polizze: PolizzeResult) {
    this.callBackendFunctionIfConfirmed(polizze, true,
      this.dataService.revokePoliceMaklerAccess.bind(this.dataService),
      'Wollen Sie dem externen Makler das Offert entziehen?',
      'Polizze ist für den externen Makler nicht mehr zugänglich');
  }

  private callBackendFunctionIfConfirmed(polizze: PolizzeResult, setInternTo: boolean,
                                         backendFunction: (polizzeId: number) => Observable<Payload<boolean>>,
                                         confirmationText: string, snackbarText: string) {
    this.messageService.showSimpleYesNoDialog(confirmationText)
      .afterClosed()
      .pipe(
        take(1), takeUntilDestroyed(this.destroyRef),
        filter(_dialogResult => _dialogResult?.status === 'yes'),
        switchMap(_ => backendFunction(polizze.id)),
        filter(_payload => this.messageService.handlePayloadReturnSuccess(_payload))
      )
      .subscribe(_payload => {
        polizze.intern = setInternTo;
        this.messageService.showSnackBar({
          text: snackbarText,
          type: MessageType.INFO
        });
      });
  }

  protected isIntern(polizze: PolizzeResult): boolean {
    return polizze.intern;
  }

  protected getPoliceLockIcon(polizze: PolizzeResult) {
    return this.isIntern(polizze)
      ? 'lock_outline'
      : 'lock_open';
  }

  protected getPoliceLockTooltip(polizze: PolizzeResult) {
    return this.isIntern(polizze)
      ? 'für externen Makler nicht sichtbar'
      : 'für externen Makler sichtbar';
  }

  protected getRejectStatus(rejectType: AntragRejectType) {
    if (rejectType === AntragRejectType.REJECTED) {
      return 'abgelehnt';
    } else if (rejectType === AntragRejectType.CHANGE) {
      return 'überarbeiten';
    }
    return '';
  }
}
